import { Check, ChevronDown, X, type LucideIcon } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { cn } from '../../lib/utils.ts';
import type { PipelineView } from '../../lib/client.ts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select.tsx';
import { StageChip } from './stage-chip.tsx';

/**
 * How a record moves between stages (RELAY-952). The three pipelines on a project page get
 * deliberately different controls, so nobody moves a deal when they meant the project:
 *
 *   Project Stage — a STEPPER per pipeline in the Pipelines card: every stage visible, the path
 *                   so far filled in, won/lost as separate end buttons.
 *   Deal Status   — the same stepper at `size="compact"` in the deal card header.
 *   Job Status    — likewise in the job card header (sky family).
 */

/** Where a pipeline ended: still open, or at a won or lost stage. */
export type StageOutcome = 'open' | 'won' | 'lost';

export function outcomeOf(
  stage: { terminalKind: 'won' | 'lost' | null } | undefined,
): StageOutcome {
  return stage?.terminalKind ?? 'open';
}

/**
 * The stepper keeps the gradual fill a pipeline was drawn with — each stage's shade (set in
 * Settings → Pipelines, 200 light to 700 deep) is how far along the process it is — but in the
 * design system's colours, chosen by outcome: the brand purple while open, success green once
 * won, destructive red once lost. So the bar says both where the record is and how it ended.
 * The shade is a strength of that colour (`color-mix`), so it follows light and dark mode.
 *
 * An archived place keeps the same bar in exactly these colours, drawn as a DISABLED control
 * is: faded (the design system's `opacity-50`), not recoloured — still readable, visibly out of
 * play.
 */
/**
 * While open, a deal's compact bar is green and a job's blue (the user): chosen by the pipeline's
 * colour family (deal = emerald, job = sky), literal values so Tailwind need not emit the theme vars.
 * Won and lost keep their outcome colours; other families (the project's violet) keep primary.
 */
const OPEN_COLOUR_BY_FAMILY: Record<string, string> = {
  emerald: 'oklch(50.8% 0.118 165.612)',
  sky: 'oklch(50% 0.134 242.749)',
};

const OUTCOME_COLOUR = {
  open: 'var(--primary)',
  won: 'var(--success)',
  lost: 'var(--destructive)',
} as const satisfies Record<StageOutcome, string>;

const SHADE_STRENGTH: Record<string, number> = {
  '200': 25,
  '300': 40,
  '400': 55,
  '500': 70,
  '600': 85,
  '700': 100,
};

const FRAME = {
  open: 'border-border',
  won: 'border-state-success-border',
  lost: 'border-state-danger-border',
} as const satisfies Record<StageOutcome, string>;

/** A reached stage's fill: its shade of the bar's colour, with text that reads on it. */
function segmentFill(
  outcome: StageOutcome,
  shade: string,
  i: number,
  of: number,
  openColour?: string,
  byPosition?: boolean,
) {
  // A shade outside the scale falls back to the stage's place in the pipeline.
  const byPlace = Math.round(25 + (75 * i) / Math.max(of - 1, 1));
  const strength = byPosition ? byPlace : (SHADE_STRENGTH[shade] ?? byPlace);
  return {
    strength,
    style: {
      backgroundColor: `color-mix(in oklab, ${outcome === 'open' ? (openColour ?? OUTCOME_COLOUR.open) : OUTCOME_COLOUR[outcome]} ${strength}%, transparent)`,
    },
    className: strength > 55 ? 'text-white' : 'text-foreground',
  };
}

/**
 * The Won / Lost buttons always say which is which by colour: green and red text while they are
 * a choice, a solid fill in the same colour once the pipeline sits there.
 */
const END = {
  won: {
    idle: 'text-state-success-fg border-state-success-border hover:bg-state-success-bg',
    at: 'bg-success border-success text-white',
  },
  lost: {
    idle: 'text-state-danger-fg border-state-danger-border hover:bg-state-danger-bg',
    at: 'bg-destructive border-destructive text-white',
  },
} as const;

export function StageStepper({
  pipeline,
  value,
  onMove,
  disabled,
  archived,
  marker,
  previousStageId,
  size = 'default',
}: {
  pipeline: PipelineView;
  value: string;
  onMove: (stageId: string) => void;
  /**
   * For a place at a won or lost stage: the stage it was in just before (`PipelinePlace.previousStageId`).
   * The bar is filled up to and including it, the stages after stay muted. Null/absent: none.
   */
  previousStageId?: string | null | undefined;
  disabled?: boolean | undefined;
  /**
   * An archived place (TS-1747): the same bar in its own colours, faded like a disabled
   * control, and nothing can be moved — the API refuses a move in an archived place until it
   * is restored.
   */
  archived?: boolean | undefined;
  /**
   * Drawn just above the stage the record is at — the segment while open, the Won or Lost
   * button once ended — so a "since" tag sits over the stage it is about. Each column carries
   * its own slot, which keeps the tag aligned however the row wraps. The tag is positioned out
   * of the flow, so a tag wider than its stage never widens it (the Won/Lost buttons keep
   * their size).
   */
  marker?: ReactNode;
  /**
   * `compact` is the same stepper in a card header (a deal's or a job's): smaller than the
   * full one (144px segments), one line per segment, no `marker`, and Won/Lost as icon-only buttons until the record sits there.
   * Every rule above (colours, fill, ring, scrolling, archived, disabled) is shared.
   */
  size?: 'default' | 'compact';
}) {
  const compact = size === 'compact';
  const open = pipeline.stages.filter((s) => !s.isTerminal);
  const groups = endGroups(pipeline.stages.filter((s) => s.isTerminal));
  const at = open.findIndex((s) => s.id === value);
  const current = pipeline.stages.find((s) => s.id === value);
  const outcome = outcomeOf(current);
  // An ended bar is filled only up to the stage before it ended; unknown (or not an open stage of
  // this pipeline) fills nothing, since filling all of it would claim progress nobody recorded.
  const endedAfter = previousStageId ? open.findIndex((s) => s.id === previousStageId) : -1;
  const locked = disabled || archived;
  // When the progress scrolls: bring the stage the record is at into the middle of the view
  // (the end of the bar once it is won or lost), and take back the scrollbar's height with a
  // negative margin, so Won/Lost stretch to exactly the bar's height and every pipeline row
  // keeps one spacing.
  // Run on load and whenever the box or its content changes size — the first paint can come
  // before the layout has settled.
  const scroller = useRef<HTMLDivElement>(null);
  const [gutter, setGutter] = useState(0);
  const [grown, setGrown] = useState<string | null>(null);
  const growIfCut = (e: SyntheticEvent<HTMLLIElement>) => {
    const label = e.currentTarget.querySelector('span');
    setGrown(
      label && label.scrollWidth > label.clientWidth
        ? (e.currentTarget.dataset.step ?? null)
        : null,
    );
  };
  useLayoutEffect(() => {
    const box = scroller.current;
    if (!box) return;
    const fit = () => {
      setGutter(box.offsetHeight - box.clientHeight);
      if (box.scrollWidth <= box.clientWidth) return;
      // A won or lost bar has no pressed segment: show where it got to, the last one filled.
      const reachedNow = box.querySelectorAll<HTMLElement>('[data-reached="true"]');
      const here =
        box.querySelector<HTMLElement>('[aria-pressed="true"]') ??
        (outcome !== 'open' ? (reachedNow[reachedNow.length - 1] ?? null) : null);
      const left = here
        ? here.offsetLeft - (box.clientWidth - here.offsetWidth) / 2
        : box.scrollWidth;
      // Glide there, so the eye follows to the stage; jump for people who asked for less motion.
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (typeof box.scrollTo === 'function')
        box.scrollTo({ left, behavior: still ? 'auto' : 'smooth' });
      else box.scrollLeft = left;
    };
    fit();
    // jsdom has no ResizeObserver.
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(fit);
    watch.observe(box);
    if (box.firstElementChild) watch.observe(box.firstElementChild);
    return () => watch.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `outcome` follows `value`
  }, [value, previousStageId]);
  // Compact has no scrollbar, so a plain mouse wheel could never move it: turn a vertical wheel over
  // it into a sideways scroll while there is room to go that way (the page scrolls at the ends).
  useEffect(() => {
    const box = scroller.current;
    if (!compact || !box) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || box.scrollWidth <= box.clientWidth) return;
      const max = box.scrollWidth - box.clientWidth;
      if ((e.deltaY < 0 && box.scrollLeft <= 0) || (e.deltaY > 0 && box.scrollLeft >= max - 1))
        return;
      e.preventDefault();
      box.scrollLeft += e.deltaY;
    };
    box.addEventListener('wheel', onWheel, { passive: false });
    return () => box.removeEventListener('wheel', onWheel);
  }, [compact]);
  const ALIGN = {
    start: 'left-0',
    center: 'left-1/2 -translate-x-1/2',
    end: 'right-0',
  } as const;
  const slot = (stageId: string, align: keyof typeof ALIGN, here = stageId === value) =>
    marker && !compact ? (
      <div className="relative h-7" data-marker-slot={stageId}>
        {here && (
          <div className={cn('absolute bottom-1.5 whitespace-nowrap', ALIGN[align])}>{marker}</div>
        )}
      </div>
    ) : null;
  return (
    <div
      role="group"
      aria-label={pipeline.name}
      // Faded like any disabled control when archived; the colours themselves stay as they are.
      className={cn('flex flex-wrap', compact ? 'gap-1.5' : 'gap-2', archived && 'opacity-50')}
      data-size={size}
      data-testid="project-stage-stepper"
      data-family={pipeline.family}
      data-outcome={outcome}
      data-archived={archived || undefined}
    >
      {/*
        Only the progress scrolls: each segment keeps a readable minimum width (144px, its name
        on up to two lines), and a long pipeline (20+ stages) scrolls sideways inside this box
        while Won/Lost stay in view. The row stretches its columns, so when a name takes two
        lines the whole bar and the Won/Lost buttons grow together and stay one even strip.
        The box never narrows below one segment, and Won and Lost are one group: on a card too
        narrow for both side by side, they move below the bar together, never one on each line
        and never squeezing the bar to a sliver.
      */}
      {compact && (
        <span
          className="text-muted-foreground flex shrink-0 items-center text-xs font-medium whitespace-nowrap"
          data-testid="stepper-name"
        >
          {pipeline.name}
        </span>
      )}
      <div
        ref={scroller}
        className={cn(
          'relative min-w-36 flex-1 overflow-x-auto overflow-y-hidden',
          // Compact: no scrollbar at all (important: app.css sets scrollbar-width on *, outside the layers) (trackpad, shift+wheel and the hover expand still scroll it).
          compact
            ? '[scrollbar-width:none]! [&::-webkit-scrollbar]:hidden'
            : '[scrollbar-width:thin]',
        )}
        style={gutter ? { marginBottom: -gutter } : undefined}
        data-testid="stage-scroller"
      >
        <ol className="flex w-full">
          {open.map((s, i) => {
            const reached = outcome === 'open' ? i <= at : i <= endedAfter;
            const fill = reached
              ? segmentFill(
                  outcome,
                  s.colour,
                  i,
                  open.length,
                  compact ? OPEN_COLOUR_BY_FAMILY[pipeline.family] : undefined,
                  compact,
                )
              : null;
            const first = i === 0;
            const last = i === open.length - 1;
            return (
              <li
                key={s.id}
                className={cn(compact ? SEGMENT_COMPACT : SEGMENT, grown === s.id && 'flex-none')}
                // Compact: a step grows to its full name only while hovered or focused AND cut with an ellipsis.
                onMouseEnter={compact ? growIfCut : undefined}
                onFocus={compact ? growIfCut : undefined}
                onMouseLeave={compact ? () => setGrown(null) : undefined}
                onBlur={compact ? () => setGrown(null) : undefined}
                data-step={s.id}
              >
                {slot(s.id, first ? 'start' : last ? 'end' : 'center')}
                <button
                  type="button"
                  disabled={locked || s.id === value}
                  aria-pressed={s.id === value}
                  aria-label={`Move to ${s.name}`}
                  // A name longer than two lines is cut with an ellipsis; hovering shows it whole.
                  title={s.name}
                  onClick={() => onMove(s.id)}
                  data-reached={reached}
                  data-strength={fill?.strength}
                  style={fill?.style}
                  className={cn(
                    'flex w-full flex-1 items-center justify-center border-y border-l text-xs font-medium transition-colors',
                    compact
                      ? 'h-[1.375rem] px-2 text-xs whitespace-nowrap'
                      : 'min-h-8 px-2 py-1 leading-tight',
                    FRAME[outcome],
                    first && 'rounded-l-md',
                    last && 'rounded-r-md border-r',
                    fill
                      ? fill.className
                      : cn('bg-muted text-muted-foreground', !archived && 'hover:bg-accent'),
                    s.id === value && 'ring-foreground/40 ring-2 ring-inset',
                  )}
                >
                  <span
                    className={
                      compact ? cn('truncate', grown === s.id && 'overflow-visible') : SEGMENT_LABEL
                    }
                  >
                    {s.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
      {groups.length > 0 && (
        <div
          className={cn('flex shrink-0 gap-2', compact && 'self-start')}
          data-testid="stage-ends"
        >
          {groups.map(({ kind, stages }, i) => {
            const chosen = stages.find((s) => s.id === value);
            const Icon = kind === 'won' ? Check : X;
            // Compact: icon only while idle (the name is in `title`), icon + name once it sits there.
            const iconOnly = compact && !chosen;
            const look = cn(
              'inline-flex flex-1 items-center justify-center gap-1 rounded-md border text-xs font-medium',
              compact
                ? 'h-[1.375rem] flex-none gap-1.5 px-2 text-xs whitespace-nowrap'
                : 'min-h-8 px-2.5',
              compact ? (chosen ? 'max-w-36 min-w-8' : 'w-9') : chosen ? END_WIDTH_AT : END_WIDTH,
              END[kind][chosen ? 'at' : 'idle'],
              // Archived, so disabled: an idle Won/Lost gives no hover answer either.
              archived && !chosen && 'hover:bg-transparent',
            );
            const only = stages.length === 1 ? stages[0] : undefined;
            return (
              <div key={kind} className="flex shrink-0 flex-col">
                {slot(chosen?.id ?? kind, i === groups.length - 1 ? 'end' : 'center', !!chosen)}
                {only ? (
                  <button
                    type="button"
                    disabled={locked || only.id === value}
                    aria-pressed={only.id === value}
                    aria-label={`Mark ${kind}: ${only.name}`}
                    onClick={() => onMove(only.id)}
                    className={look}
                    title={only.name}
                  >
                    <Icon className="size-3.5 shrink-0" />
                    {!iconOnly && <span className={END_LABEL}>{only.name}</span>}
                  </button>
                ) : (
                  <EndMenu
                    kind={kind}
                    stages={stages}
                    chosen={chosen}
                    disabled={locked}
                    onMove={onMove}
                    className={look}
                    iconOnly={iconOnly}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * A progress segment: at least 144px wide, its name on up to two lines (a long single word
 * breaks too) and cut with an ellipsis past that, the whole name in `title`. Segments grow to
 * the bar's tallest, so the bar stays one even strip.
 */
const SEGMENT = 'flex min-w-36 flex-1 flex-col';
const SEGMENT_LABEL = 'line-clamp-2 text-center break-words';
/** The compact stepper's segment: at least 144px, one line cut with an ellipsis; hovered or focused it grows to its full name (the bar scrolls if it must). */
const SEGMENT_COMPACT = 'flex min-w-36 flex-1 flex-col';

const KIND_LABEL = { won: 'Won', lost: 'Lost' } as const;

/**
 * Every Won/Lost button has one short, fixed width — a plain one or a menu, whichever pipeline —
 * so they line up from row to row and never change size as the choice changes. It is the
 * smallest that fits "Won"/"Lost" with the menu's arrow. The one the pipeline sits at keeps
 * that width while its name fits, and only grows when the chosen name is longer ("Won – partial
 * scope"), up to 144px; a longer name is cut with an ellipsis (the whole name in `title`), so
 * the stepper keeps room. The menu cuts a long stage name the same way.
 */
const END_WIDTH = 'w-22';
const END_WIDTH_AT = 'min-w-22 max-w-36';
const END_LABEL = 'min-w-0 flex-1 truncate text-center';

/** The terminal stages by kind, in the order the pipeline lists them. */
function endGroups(ends: PipelineView['stages']) {
  const groups: { kind: 'won' | 'lost'; stages: PipelineView['stages'] }[] = [];
  for (const s of ends) {
    if (!s.terminalKind) continue;
    const group = groups.find((g) => g.kind === s.terminalKind);
    if (group) group.stages.push(s);
    else groups.push({ kind: s.terminalKind, stages: [s] });
  }
  return groups;
}

/**
 * Several stages of one kind (Won, Won 2…) share one button: it opens a menu of them and reads
 * the one the record is at, or the kind while it is at none. Its width is the fixed
 * `END_WIDTH`, like a plain Won/Lost button.
 */
function EndMenu({
  kind,
  stages,
  chosen,
  disabled,
  onMove,
  className,
  iconOnly,
}: {
  iconOnly?: boolean | undefined;
  kind: 'won' | 'lost';
  stages: PipelineView['stages'];
  chosen: PipelineView['stages'][number] | undefined;
  disabled?: boolean | undefined;
  onMove: (stageId: string) => void;
  className: string;
}) {
  const Icon = kind === 'won' ? Check : X;
  const label = chosen?.name ?? KIND_LABEL[kind];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`Mark ${kind}: ${label}`}
          title={label}
          data-chosen={chosen?.id}
          className={className}
        >
          <Icon className="size-3.5 shrink-0" />
          {!iconOnly && <span className={END_LABEL}>{label}</span>}
          {!iconOnly && <ChevronDown className="size-3.5 shrink-0 opacity-70" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-64">
        {stages.map((s) => (
          <DropdownMenuItem
            key={s.id}
            onSelect={() => s.id !== chosen?.id && onMove(s.id)}
            data-stage-id={s.id}
            title={s.name}
          >
            {s.id === chosen?.id ? <Check className="size-4" /> : <span className="size-4" />}
            <span className="min-w-0 truncate">{s.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function StatusPicker({
  pipeline,
  value,
  onChange,
  icon: Icon,
  disabled,
  className,
  compact,
}: {
  pipeline: PipelineView;
  value: string;
  onChange: (stageId: string) => void;
  icon: LucideIcon;
  disabled?: boolean | undefined;
  className?: string | undefined;
  /** Drop the visible pipeline name where the context already says it (a board card). */
  compact?: boolean | undefined;
}) {
  const current = pipeline.stages.find((s) => s.id === value);
  return (
    <Select
      value={value}
      onValueChange={(v) => v !== value && onChange(v)}
      disabled={disabled ?? false}
    >
      <SelectTrigger
        aria-label={pipeline.name}
        className={cn('h-8 w-auto gap-1.5 px-2', className)}
        data-family={pipeline.family}
      >
        <Icon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
        {!compact && <span className="text-muted-foreground text-xs">{pipeline.name}</span>}
        {current ? (
          <StageChip
            family={pipeline.family}
            colour={current.colour}
            name={current.name}
            terminalKind={current.terminalKind}
          />
        ) : (
          <span className="text-xs">—</span>
        )}
      </SelectTrigger>
      <SelectContent>
        {pipeline.stages.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            <StageChip
              family={pipeline.family}
              colour={s.colour}
              name={s.name}
              terminalKind={s.terminalKind}
            />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
